import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Axios from 'axios';
import { Auth } from 'aws-amplify';
import Head from 'src/layout/head/Head';
import { API_ROUTES } from 'src/config/api';
import { getScheduleLabel, getPlaybookDescription, getPlaybookSchedule } from './data';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PlayIcon,
  PauseIcon,
  ClockIcon,
  CommandLineIcon,
  SparklesIcon,
  XMarkIcon,
  CheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

const PlaybookCard = ({ playbook }) => {
  const description = getPlaybookDescription(playbook);
  const schedule = getPlaybookSchedule(playbook);
  const enabled = playbook.meta?.enabled ?? playbook.active;
  const stepsCount = playbook.nodes?.length || 0;
  const lastRun = playbook.meta?.lastRun;
  const isGenerating = playbook.latest_request_status === 'pending' || playbook.latest_request_status === 'processing';
  const needsRefinement = playbook.latest_request_status === 'refinement_required';

  return (
    <Link
      to={`/playbooks/playbook/${playbook.id}`}
      className="block"
    >
      <div className={`border rounded-lg p-6 hover:shadow-lg transition-shadow duration-200 cursor-pointer ${isGenerating ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-300 dark:border-violet-700' : needsRefinement ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="items-center mb-2 w-full">
              <div className='float-right'>
                {isGenerating ? (
                  <span className="clearfix h-auto inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300">
                    <ArrowPathIcon className="w-3 h-3 animate-spin" />
                    {stepsCount > 0 ? 'Re-generating' : 'Generating'}
                  </span>
                ) : needsRefinement ? (
                  <span className="clearfix h-auto inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-amber-300 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                    <SparklesIcon className="w-3 h-3" />
                    Needs input
                  </span>
                ) : enabled ? (
                  <span className="clearfix h-auto inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                    <PlayIcon className="w-3 h-3" />
                    Enabled
                  </span>
                ) : (
                  <span className="clearfix h-auto inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300">
                    <PauseIcon className="w-3 h-3" />
                    Disabled
                  </span>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {playbook.name}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              {isGenerating ? 'AI is building your workflow...' : description}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <CommandLineIcon className="w-4 h-4" />
              {stepsCount} steps
            </span>
            <span className="flex items-center gap-1">
              <ClockIcon className="w-4 h-4" />
              {getScheduleLabel(schedule)}
            </span>
          </div>
          <span className="text-xs">
            {playbook.version_id}
          </span>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 flex items-center justify-between">
          <span>Last run: <strong>{lastRun ? moment(lastRun).format('MMM D, YYYY HH:mm') : 'Never'}</strong></span>
          {(playbook.created_by_first_name || playbook.created_by_email) && (
            <span>by <strong>{playbook.created_by_first_name ? `${playbook.created_by_first_name} ${playbook.created_by_last_name || ''}`.trim() : playbook.created_by_email}</strong></span>
          )}
        </div>
      </div>
    </Link>
  );
};

const FREQUENCY_OPTIONS = [
  { value: 'on_event', label: 'On event trigger' },
  { value: 'every_5_minutes', label: 'Every 5 minutes' },
  { value: 'every_15_minutes', label: 'Every 15 minutes' },
  { value: 'every_hour', label: 'Every hour' },
  { value: 'every_6_hours', label: 'Every 6 hours' },
  { value: 'every_day', label: 'Every day' },
  { value: 'every_week', label: 'Every week' },
  { value: 'every_month', label: 'Every month' },
];

const STEPS = [
  { id: 1, name: 'Playbook Details', icon: SparklesIcon },
  { id: 2, name: 'Generate', icon: PlayIcon },
];

const StepIndicator = ({ steps, currentStep }) => {
  return (
    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
      <nav aria-label="Progress">
        <ol className="flex items-center justify-center">
          {steps.map((step, stepIdx) => (
            <li key={step.id} className={`relative ${stepIdx !== steps.length - 1 ? 'pr-20' : ''}`}>
              {stepIdx !== steps.length - 1 && (
                <div className="absolute top-4 left-8 -right-12 h-0.5">
                  <div className={`h-full ${currentStep > step.id ? 'bg-violet-600' : 'bg-gray-200 dark:bg-gray-600'}`} />
                </div>
              )}
              <div className="relative flex flex-col items-center group">
                <span
                  className={`w-8 h-8 flex items-center justify-center rounded-full border-2 ${currentStep > step.id
                      ? 'bg-violet-600 border-violet-600'
                      : currentStep === step.id
                        ? 'border-violet-600 bg-white dark:bg-gray-800'
                        : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                >
                  {currentStep > step.id ? (
                    <CheckIcon className="w-4 h-4 text-white" />
                  ) : (
                    <step.icon className={`w-4 h-4 ${currentStep === step.id ? 'text-violet-600' : 'text-gray-400'}`} />
                  )}
                </span>
                <span className={`mt-2 text-xs font-medium ${currentStep >= step.id ? 'text-violet-600 dark:text-violet-400' : 'text-gray-500 dark:text-gray-400'
                  }`}>
                  {step.name}
                </span>
              </div>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
};

const PlaybooksList = () => {
  const history = useHistory();
  const { organisation } = useSelector((state) => state);
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    frequency: 'on_event',
    description: ''
  });

  const fetchPlaybooks = useCallback(async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      const result = await Axios(
        API_ROUTES.playbooks['playbooks:list'](),
        {
          method: 'get',
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
          params: { organisation_id: organisation.organisation },
        }
      );
      setPlaybooks(result.data.data || []);
    } catch (error) {
      console.error('Failed to fetch playbooks:', error);
    } finally {
      setLoading(false);
    }
  }, [organisation.organisation]);

  useEffect(() => {
    if (organisation.organisation) {
      fetchPlaybooks();
    }
  }, [organisation.organisation, fetchPlaybooks]);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) {
      setFormData({ name: '', frequency: 'on_event', description: '' });
      setCurrentStep(1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const user = await Auth.currentAuthenticatedUser();

      const result = await Axios(
        API_ROUTES.playbooks['playbook:create'](),
        {
          method: 'post',
          data: {
            name: formData.name,
            frequency: formData.frequency,
            description: formData.description,
            organisation_id: organisation.organisation,
          },
          headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        }
      );

      const { id, request_id } = result.data.data;
      toggleModal();
      history.push(`/playbooks/playbook/${id}?request_id=${request_id}`);
    } catch (error) {
      console.error('Failed to create playbook:', error);
    } finally {
      setCreating(false);
    }
  };

  const isStep1Valid = formData.name && formData.description;

  const filteredPlaybooks = playbooks.filter(playbook => {
    const description = getPlaybookDescription(playbook);
    const tags = playbook.meta?.tags || [];
    const enabled = playbook.meta?.enabled ?? playbook.active;

    const matchesSearch = playbook.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesFilter = filterStatus === 'all' ||
      (filterStatus === 'enabled' && enabled) ||
      (filterStatus === 'disabled' && !enabled);

    return matchesSearch && matchesFilter;
  });

  return (
    <React.Fragment>
      <Head title="Playbooks" />
      <div>
        {/* Header Section */}
        <button
          onClick={toggleModal}
          className="float-right items-center px-4 py-2 bg-violet-800 hover:bg-violet-600 text-white rounded-md text-sm font-medium transition-colors"
        >
          <PlusIcon className="w-4 h-4 inline" />
          Create Playbook
        </button>

        {/* Create Playbook Modal */}
        <Modal isOpen={modalOpen} toggle={toggleModal} size="lg">
          <ModalHeader
            toggle={toggleModal}
            className="bg-violet-800 text-xl text-white pt-4 pb-3"
            close={
              <button className="-mt-1 text-white" onClick={toggleModal}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            }
          >
            <span className="flex items-center gap-2">
              <SparklesIcon className="h-5 w-5" />
              Create New Playbook
            </span>
          </ModalHeader>

          {/* Step Indicator */}
          <StepIndicator steps={STEPS} currentStep={currentStep} />

          <form onSubmit={handleSubmit}>
            <ModalBody className="p-6">
              {/* Step 1: Playbook Details */}
              {currentStep === 1 && (
                <div className="space-y-6">
                  {/* Name Field */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Playbook Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Payment Confirmation Flow"
                      className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      required
                    />
                  </div>

                  {/* Frequency Field */}
                  <div>
                    <label htmlFor="frequency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      How often should this run?
                    </label>
                    <select
                      id="frequency"
                      name="frequency"
                      value={formData.frequency}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    >
                      {FREQUENCY_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description Field - LLM Style */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Describe what you want this playbook to do
                    </label>
                    <div className="relative">
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Describe your automation in natural language. For example: 'When a payment is received, look up the deal in Pipedrive, create an invoice in Xero, and send a confirmation email to the customer.'"
                        rows={6}
                        className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 resize-none"
                        required
                      />
                      <div className="absolute bottom-3 right-3 flex items-center gap-1 text-xs text-gray-400">
                        <SparklesIcon className="h-4 w-4" />
                        AI-powered
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Our AI will analyse your description, generate the workflow steps, and determine what credentials are needed.
                    </p>
                  </div>
                </div>
              )}

              {/* Step 2: Generate */}
              {currentStep === 2 && (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center mb-4">
                    <SparklesIcon className="w-8 h-8 text-violet-600 dark:text-violet-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Ready to Generate
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                    Our AI will analyse your description, create the workflow steps, and identify the credentials you'll need to provide.
                  </p>

                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 text-left max-w-md mx-auto">
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Summary
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Name:</span>
                        <span className="font-medium text-gray-900 dark:text-white">{formData.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Frequency:</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {FREQUENCY_OPTIONS.find(o => o.value === formData.frequency)?.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </ModalBody>

            <ModalFooter className="bg-light">
              {currentStep === 1 && (
                <>
                  <button
                    type="button"
                    onClick={toggleModal}
                    className="btn btn-outline-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!isStep1Valid}
                    className="btn bg-violet-700 hover:bg-violet-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </>
              )}
              {currentStep === 2 && (
                <>
                  <button
                    type="button"
                    onClick={handleBack}
                    className="btn btn-outline-secondary"
                    disabled={creating}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="btn bg-violet-700 hover:bg-violet-800 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {creating ? (
                      <>
                        <ArrowPathIcon className="w-4 h-4 inline mr-1 -mt-0.5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <SparklesIcon className="w-4 h-4 inline mr-1 -mt-0.5" />
                        Generate Playbook
                      </>
                    )}
                  </button>
                </>
              )}
            </ModalFooter>
          </form>
        </Modal>

        {/* Search and Filter Bar */}
        <div className="mb-6 flex items-center gap-4 flex-wrap clearfix h-auto">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search playbooks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${filterStatus === 'all'
                  ? 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              All ({playbooks.length})
            </button>
            <button
              onClick={() => setFilterStatus('enabled')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${filterStatus === 'enabled'
                  ? 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              Enabled ({playbooks.filter(p => p.meta?.enabled ?? p.active).length})
            </button>
            <button
              onClick={() => setFilterStatus('disabled')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${filterStatus === 'disabled'
                  ? 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-300'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
            >
              Disabled ({playbooks.filter(p => !(p.meta?.enabled ?? p.active)).length})
            </button>
          </div>
        </div>


        {/* Playbooks Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center text-center py-32">
            <div className="mb-8">
              <div className="w-24 h-24 rounded-full bg-violet-700 flex items-center justify-center mx-auto shadow-lg shadow-violet-200 dark:shadow-violet-900/50">
                <svg className="w-12 h-12 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Loading playbooks</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md">Fetching your playbooks and workflow configurations.</p>
          </div>
        ) : filteredPlaybooks.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <CommandLineIcon className="mx-auto h-12 w-12 text-violet-900" />
            <h3 className="mt-2 text-xl font-normal text-violet-900 font-mono uppercase dark:text-white">No playbooks found</h3>
            <p className="mt-1 text-md text-gray-500 dark:text-gray-400">
              {searchQuery ? 'Try adjusting your search criteria' : 'Get started by creating a new playbook'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6 mt-4 clearfix">
            {filteredPlaybooks.map((playbook) => (
              <PlaybookCard key={playbook.id} playbook={playbook} />
            ))}
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default PlaybooksList;
